import React from 'react';
import Head from 'next/head';
import { NextRouter, useRouter } from 'next/router';

import useUI from 'hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import PublicEnv from 'shared/utils/public-env';
import { useIsFirstRender } from 'src/hooks/use-is-first-render';

const getDispensaryPathEquivalent = (path: string): string => {
  const splitPath = path.split('/');
  splitPath.splice(1, 1, `dispensary`);

  return splitPath.join('/');
};

type SanitizePathForSeo = {
  sanitizedPath: string;
};

function sanitizePathForSeo({ asPath }: Partial<NextRouter>): SanitizePathForSeo {
  let sanitizedPath = asPath ?? '';

  /* 
    We need to sanitized the path and remove the query params.
    For seo purposes they are unecessarily creating duplicate indices.
  */
  if (asPath) {
    [sanitizedPath] = asPath.split('?');
  }
  return { sanitizedPath };
}

export default function CanonicalTag(): JSX.Element | null {
  const router = useRouter();
  const UI = useUI();
  const { dispensary } = useDispensary();
  const isFirstRender = useIsFirstRender();

  const getCanonicalUrl = (): string => {
    const hasCustomDomain = !!dispensary?.customDomainSettings?.domain;
    const { sanitizedPath } = sanitizePathForSeo(router);

    /*
      If viewing storeFront variant (regardless of domain it's served from), prefer dispensary's custom
      domain as canonical, if it exists. In all other cases (dutchie.com, no custom domain exists, etc.),
      default to marketplace URL from env.
    */
    if (UI.isVariant(`store-front`) && hasCustomDomain) {
      const customDomain: string = dispensary.customDomainSettings.domain;
      return `https://${customDomain}${sanitizedPath}`;
    }

    // For SEO, we want /stores, /embedded-menu, and /kiosks to link back to their /dispensary equivalents
    if (UI.isVariant(`store-front`) || UI.isVariant(`embedded`) || UI.isVariant(`kiosk`)) {
      return `${PublicEnv.consumerUrl}${getDispensaryPathEquivalent(sanitizedPath)}`;
    }

    // All other cases, we self-reference
    return `${PublicEnv.consumerUrl}${sanitizedPath}`;
  };

  // don't show on first render or whe nthe router isn't ready
  // we can't do first render because we can't do this while rendering build-time templates. since it's not in the build time template html, it can't be on first render, because that will cause hydration mismatch issues
  // instead, we need to wait for 2nd render + router ready
  // we can't just check if dispensary is there because this needs to work on non-dispo pages too
  if (isFirstRender || !router.isReady) {
    return null;
  }

  return (
    <Head>
      <link rel='canonical' href={getCanonicalUrl()} />
    </Head>
  );
}
